// src/components/ui.tsx — small shared pieces.
import type { ReactNode } from "react";
import { MONTHS, type Tone } from "../lib/format";

export function PageFrame(props: { title: string; lede?: ReactNode; panel?: ReactNode; children: ReactNode }) {
  return (
    <div className={props.panel ? "page with-panel" : "page"}>
      <header className="page-head">
        <h1>{props.title}</h1>
        {props.lede && <p className="lede">{props.lede}</p>}
      </header>
      {props.children}
      {props.panel}
    </div>
  );
}

/** Mono, small, sage: the section label the design system calls for. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="section-label">{children}</h3>;
}

export function Chip({ children, tone }: { children: ReactNode; tone?: "muted" | "warn" | "info" }) {
  return <span className={`chip ${tone ?? ""}`}>{children}</span>;
}

export function Margin({ value, tone, estimated }: { value: string; tone: Tone | null; estimated?: boolean }) {
  return (
    <span className={`margin ${tone ?? ""} ${estimated ? "estimated" : ""}`}>
      {value}
      {estimated && <span className="est-tag"> estimated</span>}
    </span>
  );
}

export function Notice({ kind, children }: { kind: "error" | "info"; children: ReactNode }) {
  return (
    <div className={`notice ${kind}`} role={kind === "error" ? "alert" : undefined}>
      {children}
    </div>
  );
}

export function Loading({ what }: { what: string }) {
  return <p className="loading">Loading {what}…</p>;
}

/** Twelve month toggles. `value` is the selected month numbers (1–12). */
export function MonthToggles(props: {
  value: number[];
  onChange: (months: number[]) => void;
  disabled?: boolean;
  label: string;
}) {
  const set = new Set(props.value);
  return (
    <div className="month-toggles" role="group" aria-label={props.label}>
      {MONTHS.map((name, i) => {
        const m = i + 1;
        const on = set.has(m);
        return (
          <button
            key={m}
            type="button"
            className={on ? "month on" : "month"}
            aria-pressed={on}
            disabled={props.disabled}
            onClick={() => {
              const next = new Set(set);
              if (on) next.delete(m);
              else next.add(m);
              props.onChange(Array.from(next).sort((a, b) => a - b));
            }}
          >
            {name}
          </button>
        );
      })}
    </div>
  );
}

export function Field(props: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label htmlFor={props.htmlFor}>{props.label}</label>
      {props.children}
      {props.hint && <p className="hint">{props.hint}</p>}
    </div>
  );
}
